// src/services/tenantInterceptor.ts

import axios from 'axios';
import authService from './auth.service';

/**
 * Multi-tenant API request interceptor
 * Automatically adds commune filtering for communal admins
 * and group filtering for group agents
 */

interface TenantFilters {
  communeId?: string;
  groupId?: string;
}

const getTenantFilters = (): TenantFilters => {
  const authState = authService.getAuthState();
  const user = authState.user;

  if (!user || !authState.isAuthenticated) {
    return {};
  }

  const filters: TenantFilters = {};

  // Add commune filter for communal admins
  if (user.staffRole === 'communal_admin' && user.assignedCommuneId) {
    filters.communeId = user.assignedCommuneId;
  }

  // Add group filter for group agents
  if (user.staffRole === 'group_agent' && user.assignedGroupId) {
    filters.groupId = user.assignedGroupId;
  }

  return filters;
};

const shouldApplyFilters = (url: string): boolean => {
  // Don't apply filters to auth endpoints
  if (url.includes('/auth/')) return false;
  
  // Don't apply filters to settings endpoints
  if (url.includes('/settings/')) return false;
  
  // Don't apply filters to commune management endpoints (for super admins)
  if (url.includes('/communes') && !url.includes('/communes/')) return false;
  
  return true;
};

/**
 * Setup axios interceptor for multi-tenant data filtering
 */
export const setupTenantInterceptor = () => {
  axios.interceptors.request.use(
    (config: any) => {
      const url = config.url || '';
      
      // Only apply filters to relevant endpoints
      if (!shouldApplyFilters(url)) {
        return config;
      }

      const filters = getTenantFilters();
      
      // Add filters to query parameters
      if (Object.keys(filters).length > 0) {
        config.params = {
          ...config.params,
          ...filters,
        };
        
        console.log(`🔒 Applied tenant filters to ${url}:`, filters);
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

/**
 * Check if current user can access specific commune data
 */
export const canAccessCommuneData = (communeId: string): boolean => {
  const authState = authService.getAuthState();
  const user = authState.user;

  if (!user || !authState.isAuthenticated) {
    return false;
  }

  // Super admin can access everything
  if (user.staffRole === 'super_admin') {
    return true;
  }

  // Communal admin can only access their assigned commune
  if (user.staffRole === 'communal_admin') {
    return user.assignedCommuneId === communeId;
  }

  return false;
};

/**
 * Check if current user can access specific group data
 */
export const canAccessGroupData = (groupId: string, communeId?: string): boolean => {
  const authState = authService.getAuthState();
  const user = authState.user;

  if (!user || !authState.isAuthenticated) {
    return false;
  }

  // Super admin can access everything
  if (user.staffRole === 'super_admin') {
    return true;
  }

  // Group agent can only access their assigned group
  if (user.staffRole === 'group_agent') {
    return user.assignedGroupId === groupId;
  }

  // Communal admin can access groups in their commune
  if (user.staffRole === 'communal_admin' && communeId) {
    return user.assignedCommuneId === communeId;
  }

  return false;
};

/**
 * Get filtered API URL based on user role
 */
export const getFilteredUrl = (baseUrl: string, additionalParams?: Record<string, any>): string => {
  const filters = getTenantFilters();
  const allParams = { ...filters, ...additionalParams };
  
  if (Object.keys(allParams).length === 0) {
    return baseUrl;
  }

  const params = new URLSearchParams();
  Object.entries(allParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, String(value));
    }
  });

  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}${params.toString()}`;
};

export default {
  setupTenantInterceptor,
  canAccessCommuneData,
  canAccessGroupData,
  getFilteredUrl,
  getTenantFilters,
};